import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Calendar,
  Clock,
  MapPin,
  Trophy,
  CheckCircle,
  Loader2,
  AlertCircle,
  IndianRupee,
  Sparkles,
  Tag,
  X,
  Percent,
  Gift,
  CreditCard,
  Banknote,
  Phone
} from 'lucide-react';
import CustomerFooter from '../../components/CustomerFooter';

import { BACKEND_URL, API } from '../../utils/apiConfig';

// Cashfree JS SDK
let cashfreeInstance = null;
const loadCashfree = async () => {
  if (cashfreeInstance) return cashfreeInstance;
  try {
    const { load } = await import('@cashfreepayments/cashfree-js');
    cashfreeInstance = await load({ mode: 'production' });
    return cashfreeInstance;
  } catch (e) {
    console.error('Failed to load Cashfree SDK:', e);
    return null;
  }
};

export default function BookingConfirmPage() {
  const { customer, isProfileComplete } = useCustomerAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const selectedGame = searchParams.get('game');
  const venueId = searchParams.get('venue_id');
  const venueName = searchParams.get('venue_name');
  const selectedDate = searchParams.get('date');
  const slotsJson = searchParams.get('slots');
  const totalAmount = parseFloat(searchParams.get('total') || '0');
  const numCourts = parseInt(searchParams.get('num_courts') || '1', 10);
  
  const selectedSlots = slotsJson ? JSON.parse(slotsJson) : [];
  
  const [loading, setLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  
  // Coupon states
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(totalAmount);
  const [paymentAvailable, setPaymentAvailable] = useState(false);
  const [payAtVenueAvailable, setPayAtVenueAvailable] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [bookingControl, setBookingControl] = useState(null);

  // Fetch available coupons on mount
  useEffect(() => {
    const fetchAvailableCoupons = async () => {
      try {
        const token = localStorage.getItem('customer_token');
        if (!token) return;
        
        const response = await axios.get(`${API}/coupons/available`, {
          params: { 
            venue_id: venueId, 
            game: selectedGame,
            booking_amount: totalAmount 
          },
          headers: { Authorization: `Bearer ${token}` }
        });
        setAvailableCoupons(response.data.coupons || []);
      } catch (error) {
        console.log('Failed to fetch coupons:', error);
      }
    };
    
    if (totalAmount > 0) {
      fetchAvailableCoupons();
    }

    // Check if online payment and pay-at-venue are available
    const checkPayment = async () => {
      try {
        const response = await axios.get(`${API}/payments/available`);
        setPaymentAvailable(response.data.available || false);
        setPayAtVenueAvailable(response.data.pay_at_venue ?? true);
      } catch (e) {
        setPaymentAvailable(false);
        setPayAtVenueAvailable(true);
      }
    };
    checkPayment();
  }, [venueId, selectedGame, totalAmount]);

  // Update final amount when discount changes
  useEffect(() => {
    setFinalAmount(totalAmount - discountAmount);
  }, [totalAmount, discountAmount]);

  // Auto-apply a coupon code shared via link (?discount=CODE)
  useEffect(() => {
    const pending = localStorage.getItem('pending_discount_code');
    if (pending && !appliedCoupon && totalAmount > 0 && customer) {
      setCouponCode(pending);
      handleApplyCoupon(pending);
      localStorage.removeItem('pending_discount_code');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAmount, customer]);

  // Fetch centralized booking-control config
  useEffect(() => {
    axios.get(`${API}/booking-control/status`)
      .then(res => setBookingControl(res.data))
      .catch(() => {});
  }, []);

  const handleApplyCoupon = async (codeArg) => {
    const codeToApply = (codeArg || couponCode || '').trim();
    if (!codeToApply) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.post(`${API}/coupons/validate`, {
        code: codeToApply.toUpperCase(),
        user_id: customer?.mobile || customer?.id,
        booking_amount: totalAmount,
        venue_id: venueId,
        game: selectedGame
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.valid) {
        setAppliedCoupon({
          code: response.data.coupon_code,
          coupon_id: response.data.coupon_id,
          discount_type: response.data.discount_type,
          discount_value: response.data.discount_value
        });
        setDiscountAmount(response.data.discount_amount);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const handleSelectSuggestedCoupon = (code) => {
    setCouponCode(code);
  };

  // Sort slots by time
  const sortedSlots = [...selectedSlots].sort((a, b) => {
    return a.start_time.localeCompare(b.start_time);
  });

  const firstSlot = sortedSlots[0];
  const lastSlot = sortedSlots[sortedSlots.length - 1];
  const duration = selectedSlots.length * 30;

  // Centralized booking-control gate (per weekday of selected slot date)
  const BC_WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const isDayBlocked = (dateStr) => {
    if (!bookingControl || !dateStr) return false;
    const { master_enabled, channels, days } = bookingControl;
    if (master_enabled === false) return true;
    if (channels && channels.app === false) return true;
    const [y, mo, d] = dateStr.split('-').map(Number);
    const wk = BC_WEEKDAY_KEYS[new Date(y, mo - 1, d).getDay()];
    if (days && days[wk] === false) return true;
    return false;
  };
  const dayBlocked = isDayBlocked(selectedDate);
  const contactNumber = bookingControl?.contact_number || '9429694401';

  const handleConfirmBooking = async (paymentMethod = 'venue') => {
    if (dayBlocked) {
      toast.error(`Bookings are closed for the selected day. Please call ${contactNumber}.`);
      return;
    }
    setLoading(true);
    
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.post(`${API}/user/bookings`, {
        game: selectedGame,
        venue: venueName,
        date: selectedDate,
        slots: sortedSlots,
        coupon_code: appliedCoupon?.code || null,
        payment_method: paymentMethod,
        num_courts: numCourts
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const booking = response.data.booking;
      setBookingDetails(booking);

      if (paymentMethod === 'online') {
        // Trigger Cashfree checkout immediately
        await triggerOnlinePayment(booking);
      } else {
        // Pay at Venue — booking is already confirmed
        setBookingComplete(true);
        toast.success('Booking confirmed!');
      }
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete booking');
    } finally {
      setLoading(false);
    }
  };

  const triggerOnlinePayment = async (booking) => {
    setPaymentLoading(true);
    try {
      const token = localStorage.getItem('customer_token');
      const amount = booking.final_amount || booking.amount;
      
      const response = await axios.post(`${API}/payments/create-order`, {
        booking_id: booking.booking_id,
        amount: parseFloat(amount),
        customer_name: customer?.name || '',
        customer_phone: customer?.mobile || '',
        customer_email: customer?.email || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.data.success) {
        toast.error(response.data.error || 'Failed to create payment order');
        setPaymentLoading(false);
        return;
      }

      const { payment_session_id } = response.data;

      const cashfree = await loadCashfree();
      if (!cashfree) {
        toast.error('Payment gateway could not be loaded. Please try again.');
        setPaymentLoading(false);
        return;
      }

      await cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: '_self'
      });

    } catch (err) {
      console.error('Payment error:', err);
      toast.error(err.response?.data?.detail || 'Payment failed. Please try again.');
      setPaymentLoading(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, 'EEEE, d MMMM yyyy');
  };

  // Booking Success View
  if (bookingComplete && bookingDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-sm text-center">
            {/* Success Animation */}
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 animate-ping opacity-20" />
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-xl shadow-emerald-500/30">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <Sparkles className="absolute -top-2 -right-2 w-8 h-8 text-yellow-400 animate-bounce" />
            </div>
            
            <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
              Booking Confirmed!
            </h1>
            <p className="text-slate-600 mb-6">
              Your court has been reserved
            </p>

            <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-6 text-left mb-6">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-purple-100">
                <span className="text-xs text-slate-500">Booking ID</span>
                <span className="font-mono font-bold text-violet-600 text-lg">
                  {bookingDetails.booking_id}
                </span>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Game</p>
                    <p className="font-semibold text-slate-900">{bookingDetails.game}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Venue & Court</p>
                    <p className="font-semibold text-slate-900">{bookingDetails.venue}</p>
                    <p className="text-sm text-violet-600 font-medium">
                      {bookingDetails.courts?.length > 1 
                        ? bookingDetails.courts.join(' + ') 
                        : bookingDetails.court}
                    </p>
                    {bookingDetails.num_courts > 1 && (
                      <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">
                        {bookingDetails.num_courts} courts
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Date & Time</p>
                    <p className="font-semibold text-slate-900">
                      {formatDisplayDate(bookingDetails.date)}
                    </p>
                    <p className="text-sm text-slate-600">
                      {bookingDetails.start_time} - {bookingDetails.end_time}
                    </p>
                    {bookingDetails.slot_count > 1 && (
                      <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">
                        {bookingDetails.slot_count} slots • {bookingDetails.slot_count * 30} mins
                      </span>
                    )}
                  </div>
                </div>

                {bookingDetails.amount > 0 && (
                  <div className="flex items-start gap-3 pt-3 border-t border-dashed border-purple-200">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                      <IndianRupee className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-500">Amount</p>
                      {bookingDetails.discount_amount > 0 ? (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 line-through text-sm">₹{bookingDetails.amount}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium flex items-center gap-1">
                              <Tag className="w-3 h-3" />
                              {bookingDetails.coupon_code}
                            </span>
                          </div>
                          <p className="font-bold text-emerald-600 text-xl flex items-center">
                            <IndianRupee className="w-5 h-5" />
                            {bookingDetails.final_amount}
                            <span className="ml-2 text-xs font-normal text-emerald-500">
                              (saved ₹{bookingDetails.discount_amount})
                            </span>
                          </p>
                        </>
                      ) : (
                        <p className="font-bold text-emerald-600 text-xl flex items-center">
                          <IndianRupee className="w-5 h-5" />
                          {bookingDetails.amount}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/my-bookings')}
                className="w-full h-12 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 hover:from-violet-600 hover:via-purple-600 hover:to-fuchsia-600 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25"
                data-testid="view-bookings-btn"
              >
                View My Bookings
              </Button>
              
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full h-12 rounded-xl border-purple-200 hover:bg-purple-50"
                data-testid="book-another-btn"
              >
                Book Another Slot
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Confirmation View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 pb-32">
      {/* Progress Steps - Vibrant */}
      <div className="bg-white/80 backdrop-blur border-b border-purple-100 px-4 py-3">
        <div className="flex items-center gap-1 text-xs">
          <span className="flex items-center gap-1 text-emerald-600">
            <span className="w-6 h-6 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 text-white flex items-center justify-center text-xs font-bold shadow-md">✓</span>
            <span className="hidden sm:inline">Game</span>
          </span>
          <div className="flex-1 h-1 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full" />
          <span className="flex items-center gap-1 text-cyan-600">
            <span className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-white flex items-center justify-center text-xs font-bold shadow-md">✓</span>
            <span className="hidden sm:inline">Venue</span>
          </span>
          <div className="flex-1 h-1 bg-gradient-to-r from-blue-400 to-violet-500 rounded-full" />
          <span className="flex items-center gap-1 text-violet-600">
            <span className="w-6 h-6 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 text-white flex items-center justify-center text-xs font-bold shadow-md">✓</span>
            <span className="hidden sm:inline">Slot</span>
          </span>
          <div className="flex-1 h-1 bg-gradient-to-r from-purple-500 to-fuchsia-500 rounded-full" />
          <span className="flex items-center gap-1 text-fuchsia-600 font-medium">
            <span className="w-6 h-6 rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white flex items-center justify-center text-xs font-bold shadow-lg ring-2 ring-fuchsia-200">4</span>
            <span className="hidden sm:inline">Confirm</span>
          </span>
        </div>
      </div>

      {/* Booking Summary */}
      <div className="px-4 py-4">
        <div className="bg-white rounded-2xl shadow-lg border border-purple-100 overflow-hidden">
          <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 p-4 text-white">
            <h2 className="font-bold text-lg">Confirm Your Booking</h2>
            <p className="text-sm text-purple-200">Review all details before confirming</p>
          </div>

          <div className="p-4 space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-violet-50 to-purple-50">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Game</p>
                <p className="font-bold text-slate-900 text-lg">{selectedGame}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-pink-50 to-rose-50">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Venue</p>
                <p className="font-bold text-slate-900 text-lg">{venueName}</p>
                {numCourts > 1 && (
                  <span className="inline-block mt-1 text-xs px-2 py-1 rounded-full bg-violet-100 text-violet-700 font-medium">
                    {numCourts} courts (adjacent, auto-assigned)
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-cyan-50 to-blue-50">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Date</p>
                <p className="font-bold text-slate-900 text-lg">{formatDisplayDate(selectedDate)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Time Slots</p>
                <p className="font-bold text-slate-900 text-lg">
                  {firstSlot?.start_time} - {lastSlot?.end_time}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">
                    {selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''}
                  </span>
                  <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700 font-medium">
                    {duration} mins
                  </span>
                </div>
              </div>
            </div>

            {/* Selected Slots Detail */}
            {selectedSlots.length > 1 && (
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-xs text-slate-500 mb-2">Individual Slots:</p>
                <div className="flex flex-wrap gap-2">
                  {sortedSlots.map((slot, idx) => (
                    <span 
                      key={idx}
                      className="text-xs px-2 py-1 rounded-lg bg-white border border-purple-200 text-purple-700"
                    >
                      {slot.start_time} - {slot.end_time}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Coupon Section */}
          <div className="mx-4 mb-4 p-4 rounded-xl bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-5 h-5 text-purple-600" />
              <span className="font-semibold text-slate-800">Apply Coupon</span>
            </div>
            
            {appliedCoupon ? (
              /* Applied Coupon Display */
              <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <div>
                    <span className="font-mono font-bold text-emerald-700">{appliedCoupon.code}</span>
                    <p className="text-xs text-emerald-600">
                      {appliedCoupon.discount_type === 'percentage' 
                        ? `${appliedCoupon.discount_value}% off` 
                        : `₹${appliedCoupon.discount_value} off`}
                      {' '}- You save ₹{discountAmount}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={handleRemoveCoupon}
                  className="p-1 hover:bg-emerald-100 rounded-full transition-colors"
                  data-testid="remove-coupon-btn"
                >
                  <X className="w-5 h-5 text-emerald-600" />
                </button>
              </div>
            ) : (
              /* Coupon Input */
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    className="flex-1 font-mono uppercase"
                    data-testid="coupon-input"
                  />
                  <Button
                    onClick={() => handleApplyCoupon()}
                    disabled={couponLoading || !couponCode.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4"
                    data-testid="apply-coupon-btn"
                  >
                    {couponLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Apply'
                    )}
                  </Button>
                </div>
                
                {/* Suggested Coupons */}
                {availableCoupons.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Gift className="w-3 h-3" /> Available coupons for you:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {availableCoupons.slice(0, 3).map((coupon) => (
                        <button
                          key={coupon.code}
                          onClick={() => handleSelectSuggestedCoupon(coupon.code)}
                          className="text-xs px-3 py-1.5 rounded-full border border-purple-300 bg-white hover:bg-purple-50 text-purple-700 transition-colors flex items-center gap-1"
                          data-testid={`suggested-coupon-${coupon.code}`}
                        >
                          <Percent className="w-3 h-3" />
                          {coupon.code}
                          {coupon.potential_discount > 0 && (
                            <span className="text-emerald-600 font-medium">-₹{coupon.potential_discount}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Price Breakdown */}
          {totalAmount > 0 && (
            <div className="mx-4 mb-4 p-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
              {/* Per-court breakdown for multi-court */}
              {numCourts > 1 && (
                <div className="flex items-center justify-between text-emerald-100 text-sm mb-2">
                  <span>{selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} x {numCourts} courts</span>
                  <span className="flex items-center">
                    <IndianRupee className="w-4 h-4" />
                    {totalAmount}
                  </span>
                </div>
              )}
              {/* Subtotal */}
              <div className="flex items-center justify-between text-emerald-100 text-sm mb-2">
                <span>Booking Amount</span>
                <span className="flex items-center">
                  <IndianRupee className="w-4 h-4" />
                  {totalAmount}
                </span>
              </div>
              
              {/* Discount */}
              {discountAmount > 0 && (
                <div className="flex items-center justify-between text-emerald-200 text-sm mb-2">
                  <span className="flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    Coupon Discount ({appliedCoupon?.code})
                  </span>
                  <span className="flex items-center text-emerald-100">
                    - <IndianRupee className="w-4 h-4" />
                    {discountAmount}
                  </span>
                </div>
              )}
              
              {/* Divider */}
              {discountAmount > 0 && (
                <div className="border-t border-emerald-400/30 my-2"></div>
              )}
              
              {/* Final Amount */}
              <div className="flex items-center justify-between">
                <span className="font-semibold">Total Payable</span>
                <span className="text-2xl font-bold flex items-center">
                  <IndianRupee className="w-6 h-6" />
                  {finalAmount}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Player Info */}
        <div className="mt-4 bg-white rounded-xl p-4 border border-purple-100 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Booked By</h3>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-lg">
                {customer?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div>
              <p className="font-semibold text-slate-900">{customer?.name}</p>
              <p className="text-sm text-slate-500">+91 {customer?.mobile}</p>
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Important</p>
            <p className="text-amber-700">Cancellation is not allowed once the booking is confirmed.</p>
          </div>
        </div>
      </div>

      {/* Bottom CTA - Payment Options */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-purple-100 p-4 shadow-2xl">
        {dayBlocked ? (
          <div className="text-center" data-testid="booking-closed-confirm">
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm font-semibold text-red-700">Bookings are currently closed for the selected day.</p>
              <a href={`tel:${contactNumber}`} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-violet-700">
                <Phone className="w-4 h-4" /> Call {contactNumber} to book
              </a>
            </div>
          </div>
        ) : paymentAvailable && finalAmount > 0 ? (
          <div className="space-y-3">
            <Button
              onClick={() => handleConfirmBooking('online')}
              disabled={loading || paymentLoading}
              className="w-full h-13 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500 hover:from-indigo-600 hover:via-blue-600 hover:to-cyan-600 text-white font-bold text-base rounded-xl shadow-lg shadow-blue-500/30"
              data-testid="pay-online-btn"
            >
              {(loading || paymentLoading) ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Pay Online - {'\u20B9'}{finalAmount}
                </>
              )}
            </Button>
            {payAtVenueAvailable && (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400">or</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <Button
                  onClick={() => handleConfirmBooking('venue')}
                  disabled={loading || paymentLoading}
                  variant="outline"
                  className="w-full h-12 rounded-xl border-2 border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold"
                  data-testid="pay-at-venue-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      <Banknote className="w-5 h-5 mr-2" />
                      Pay at Venue - {'\u20B9'}{finalAmount}
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        ) : payAtVenueAvailable ? (
          <Button
            onClick={() => handleConfirmBooking('venue')}
            disabled={loading}
            className="w-full h-14 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 hover:from-violet-600 hover:via-purple-600 hover:to-fuchsia-600 text-white font-bold text-lg rounded-xl shadow-lg shadow-purple-500/30"
            data-testid="confirm-booking-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Confirming...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Confirm Booking
                {totalAmount > 0 && (
                  <span className="ml-2 flex items-center">
                    • <IndianRupee className="w-4 h-4" />{finalAmount}
                  </span>
                )}
              </>
            )}
          </Button>
        ) : (
          <div className="text-center py-3 text-slate-500 text-sm">
            Booking is currently unavailable. Please contact the venue.
          </div>
        )}
      </div>
      
      {/* Footer */}
      <CustomerFooter />
    </div>
  );
}
